import {
  Component,
  Prop,
  h,
  State,
  Element,
  Event,
  type EventEmitter,
  Listen,
  getAssetPath,
} from '@stencil/core';
import { mapWidgetData } from '../../utils/map-widget-data';
import { widgetData } from '../../assets/offsites-assets/config.json';
import { FullWidthWidget } from '../../functional-components/offsites-widget-components';
import { isMobile } from '../../../../../utils/media';
import type { AnalyticsGenericEvent } from '../../../../../utils/analytics/AnalyticsGenericEvent';
import { OffsitesWidgetV4EventsLayer } from '../widget-events/offsites-widget-v4-events-layer';

@Component({
  tag: 'uiaas-offsites-widget-v4',
  styleUrl: 'offsites-widget-v4.scss',
  shadow: true,
  assetsDirs: ['assets/offsites-assets-v4'],
})
export class UiaasOffsitesWidgetV4 {
  private offsiteWidgetData = widgetData;

  /**
   * Get related product for banners
   */
  @State() relatedProduct: string;

  @Prop({ mutable: true }) relatedProductName?: string;

  /**
   * Country code, used to retrieve data from config file
   */
  @Prop() readonly country: string;

  /**
   * Language name, used to retrieve data from config file
   */
  @Prop() readonly language: string;

  /**
   * Search term, used to retrieve data from config file
   */
  @Prop({ mutable: true, reflect: true }) term: string;

  /**
   * Brand name, used to retrieve data from config file
   */
  @Prop() readonly brand: string = 'se';

  /**
   * Name of te TAB from UIAAS used for widget stats
   */
  @Prop({ mutable: true }) tabName: string = '';

  /**
   * Currently selected Item
   */
  @State() currentTab;

  @Prop() readonly isDashboardView: boolean = false;
  @Prop() readonly analyticsTabName?: string;
  private searchStartedEv: void;
  private backgroundImagePath: string;

  @Element()
    el: HTMLElement;

  @Event({
    eventName: 'search:widgetEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    statisticsEventEmitter: EventEmitter<AnalyticsGenericEvent>;

  connectedCallback() {
      if (!this.tabName) {
        const closestTabEl = this.el.closest(
          'uiaas-search-tab,dashboard-tab-view-v2',
        );
        if (closestTabEl) {
          this.tabName = closestTabEl['tabName'];
        }
      }
      this.searchStartedEv = this.el
        .closest('uiaas-search-tab')
        ?.addEventListener('search:started', (e) => {
            this.term = e.detail.term;
        });
  }

  @Listen('search:tabClick', { target: 'window' })
  handleCurrentTab(event) {
      this.currentTab = event.detail.tabName; // Get second part of event tab name
  }

  @Listen('search:SearchResultsRelatedProduct', { target: 'window' })
  handleRelatedProduct(event) {
      // Check if relatedProductName exist, if not, load it from event
      if (!this.relatedProductName)
        this.relatedProductName = event.detail.relatedProduct;
  }
  private emitSearchStatisticsEvent = (e: AnalyticsGenericEvent) => {
      this.statisticsEventEmitter.emit(e);
  };

  disconnectedCallback() {
      this.searchStartedEv = this.el
        .closest('uiaas-search-tab')
        ?.removeEventListener('search:started', (e) => {
            this.term = e.detail.term;
        });
  }

  private mapLinks(data) {
      const links = [];
      for (let i = 1; i <= 3; i++) {
        if (data[`link${i}value`] && data[`link${i}url`]) {
          links.push({ value: data[`link${i}value`], url: data[`link${i}url`] });
        }
      }

      return links;
  }

  render() {
      const data = mapWidgetData(
        this.offsiteWidgetData,
        this.country,
        this.language,
        this.term,
        this.brand,
        this.tabName,
        this.relatedProductName,
      );
      const imageSuffix = isMobile() ? '_small' : '';

      if (data.backgroundUrl) {
        let backgroundUrlV4 = data.backgroundUrl?.replace(
          'offsites-assets',
          'offsites-assets-v4',
        ).replace('/images', '');
        backgroundUrlV4 = backgroundUrlV4.replace('.jpg', `${imageSuffix}.jpg`);
        this.backgroundImagePath = getAssetPath(backgroundUrlV4);
      }
      const currentTabName =
      this.currentTab ??
      document.querySelector('uiaas-search-tab,dashboard-tab-view-v2')?.[
        'tabName'
      ];

      if (Object.keys(data).length && currentTabName === this.tabName) {
        data.links = this.mapLinks(data);
        return (
          <OffsitesWidgetV4EventsLayer
            eventEmitter={this.statisticsEventEmitter}
            tabName={this.analyticsTabName ?? this.tabName}
            widgetName={data.widgetName}
          >
            <FullWidthWidget
              data={data}
              tabName={this.tabName}
              isDashboard={this.isDashboardView}
              isRtl={false}
              computedPlacement={false}
              backgroundImagePath={this.backgroundImagePath}
            />
          </OffsitesWidgetV4EventsLayer>
        );
      }
  }
}
